import React, { useEffect, useMemo, useState } from "react";
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from "./Navigation.module.css";
import { formatFixed } from '../../utils/numberFormat';
import {
  getScheduleProgressOverrideSeriesForInstance,
  getScheduleCurveMetaForInstance,
  type ScheduleProgressPoint,
} from "../../Data/ScheduleProgress/scheduleProgressOverrides";
import { getAllProjectsSeedPlusAdded } from "../../Data/ProjectConfig";
import { getProjectKpiConfigOverride } from "../../Data/ProjectConfig/projectKpiConfigOverrides";
import {
  computeSpiHistorySeries,
  getCurrentSpiAsOf,
  getSpiColorBand,
  SPI_TOLERANCE_DEFAULTS,
  type SpiHistoryPoint,
} from "../../kpi-engine/schedule";
import PerformanceIndexGauge from "./PerformanceIndexGauge";
import { toISODate } from './thermometerUtils';
import {
  indexColorHex,
  PerformanceIndexHistoryChart,
  VarianceMiniRow,
  VarianceModalOverlay,
  type VarianceHistoryPoint,
} from './varianceChartShared';

// ---------------------------------------------------------------------------
// ScheduleVariance — renders an SPI gauge + SPI history chart.
//
// Registered to catalog GUID "kpi_sched_variance_v1".
// Reads the same schedule curve data as ScheduleThermometer (no new data
// model required).
// ---------------------------------------------------------------------------

type Props = {
  projectGUID?: string;
  currentUserGuid?: string;
  instanceGuid?: string;
  title?: string;
  displayMode?: "full" | "mini";
  /** Pre-fetched series data from adapter. When provided, skips internal localStorage read. */
  prefetchedSeries?: ScheduleProgressPoint[];
  /** Pre-fetched SPI thresholds from adapter. When provided, skips internal config read. */
  prefetchedSpiThresholds?: { warnThresh: number; critThresh: number };
  /** Per-instance minimum baseline % before SPI activates. Overrides the global default. */
  spiMinBaselinePct?: number;
  /** When true, renders without the outer scheduleChartWrap border/background (parent provides it). */
  embedded?: boolean;
};

/* --- Main component ------------------------------------------------------ */

export default function ScheduleVariance({
  projectGUID,
  instanceGuid,
  title,
  displayMode,
  prefetchedSeries,
  prefetchedSpiThresholds,
  spiMinBaselinePct,
  embedded,
}: Props) {
  const { t } = useAppTranslation('kpi');
  const [dataVersion, setDataVersion] = useState(0);
  const [fullOpen, setFullOpen] = useState(false);

  useEffect(() => {
    const onChange = () => setDataVersion((v) => v + 1);
    window.addEventListener("l4e_schedule_progress_changed" as any, onChange);
    return () =>
      window.removeEventListener(
        "l4e_schedule_progress_changed" as any,
        onChange,
      );
  }, []);

  // --- resolve project GUID ---
  const projectGuidNorm = useMemo(() => {
    if (!projectGUID) return "";
    const all = getAllProjectsSeedPlusAdded() || [];
    const g = String(projectGUID).toUpperCase();
    const rec = all.find(
      (p: any) => String(p?.project_GUID || p?.project_guid || "").toUpperCase() === g,
    );
    return String(
      (rec as any)?.project_GUID ||
        (rec as any)?.project_guid ||
        projectGUID ||
        "",
    ).trim();
  }, [projectGUID]);

  const instanceGuidNorm = String(instanceGuid || "").trim();

  // --- load schedule series (same data as ScheduleThermometer) ---
  // Prefer pre-fetched data from adapter; fall back to direct localStorage read.
  const series: ScheduleProgressPoint[] = useMemo(() => {
    if (prefetchedSeries && dataVersion === 0) return prefetchedSeries;
    void dataVersion;
    if (!projectGuidNorm) return [];
    const ov = getScheduleProgressOverrideSeriesForInstance(
      projectGuidNorm,
      instanceGuidNorm || undefined,
    );
    return ov || [];
  }, [prefetchedSeries, projectGuidNorm, instanceGuidNorm, dataVersion]);

  // --- read SPI thresholds from instance config ---
  // Prefer pre-fetched thresholds from adapter; fall back to direct localStorage read.
  const { warnThresh, critThresh } = useMemo(() => {
    void dataVersion; // intentional recompute on data bump (config read is via stable refs)
    if (prefetchedSpiThresholds) return prefetchedSpiThresholds;
    if (!projectGuidNorm) return { warnThresh: SPI_TOLERANCE_DEFAULTS.warning_threshold, critThresh: SPI_TOLERANCE_DEFAULTS.critical_threshold };
    try {
      const ov = getProjectKpiConfigOverride(projectGuidNorm as any);
      const cats = (ov as any)?.category_instances || {};
      const schedArr: any[] = (cats as any)?.schedule || [];
      const inst = schedArr.find(
        (x: any) =>
          String(x?.instance_guid || "") === instanceGuidNorm ||
          String(x?.catalog_kpi_guid || "").toLowerCase() === "kpi_sched_variance_v1",
      );
      return {
        warnThresh:
          inst?.spi_warning_threshold ?? SPI_TOLERANCE_DEFAULTS.warning_threshold,
        critThresh:
          inst?.spi_critical_threshold ?? SPI_TOLERANCE_DEFAULTS.critical_threshold,
      };
    } catch {
      return { warnThresh: SPI_TOLERANCE_DEFAULTS.warning_threshold, critThresh: SPI_TOLERANCE_DEFAULTS.critical_threshold };
    }
  }, [prefetchedSpiThresholds, projectGuidNorm, instanceGuidNorm, dataVersion]);

  // --- compute SPI series (current + past only, no future) ---
  // Effective early-maturity guard: per-instance override > global default.
  const effectiveGuardPct = (typeof spiMinBaselinePct === "number" && Number.isFinite(spiMinBaselinePct))
    ? spiMinBaselinePct
    : SPI_TOLERANCE_DEFAULTS.early_maturity_guard_pct;

  const { spiHistory, spiDiag } = useMemo(() => {
    const diag = {
      seriesLength: series.length,
      pastDateCount: 0,
      futureDateCount: 0,
      belowGuardCount: 0,
      nullActualCount: 0,
      maxBaseline: 0,
      effectiveGuardPct,
      firstDate: "",
      lastDate: "",
      today: "",
    };
    if (!series.length) return { spiHistory: [] as SpiHistoryPoint[], spiDiag: diag };
    const today = toISODate(new Date());
    diag.today = today;
    if (series.length) {
      const sorted = [...series].sort((a, b) => a.reporting_date.localeCompare(b.reporting_date));
      diag.firstDate = sorted[0]?.reporting_date || "";
      diag.lastDate = sorted[sorted.length - 1]?.reporting_date || "";
    }
    const pastRows = series.filter((r) => r.reporting_date <= today);
    diag.pastDateCount = pastRows.length;
    diag.futureDateCount = series.length - pastRows.length;
    diag.nullActualCount = pastRows.filter((r) => r.actual_complete === null || r.actual_complete === undefined).length;
    const mapped = pastRows
      .map((r) => ({
        project_GUID: projectGuidNorm,
        reporting_date: r.reporting_date,
        baseline_complete: Number(r.baseline_complete) || 0,
        actual_complete: Number(r.actual_complete) || 0,
      }));
    diag.belowGuardCount = mapped.filter((r) => r.baseline_complete < effectiveGuardPct).length;
    diag.maxBaseline = mapped.reduce((mx, r) => Math.max(mx, r.baseline_complete), 0);
    const history = computeSpiHistorySeries(mapped, {
      warning_threshold: warnThresh,
      critical_threshold: critThresh,
      early_maturity_guard_pct: effectiveGuardPct,
    });
    // Diagnostic console output to trace SPI data-flow issues.
    if (!history.length && series.length) {
      console.warn("[ScheduleVariance] SPI history is empty despite having series data.", diag);
    }
    return { spiHistory: history, spiDiag: diag };
  }, [series, warnThresh, critThresh, projectGuidNorm, effectiveGuardPct]);

  const todayISO = toISODate(new Date());

  // Build a human-readable reason when SPI has no data despite series existing.
  const spiEmptyReason = useMemo(() => {
    if (spiHistory.length > 0) return "";
    if (!series.length) return "No schedule progress data available. Open Manage Curve to generate a baseline.";
    const d = spiDiag;
    if (d.pastDateCount === 0 && d.futureDateCount > 0) {
      return `All ${d.futureDateCount} data point(s) are after today (${d.today}). Earliest date: ${d.firstDate}. Re-open Manage Curve and Apply with the current project date range.`;
    }
    if (d.pastDateCount > 0 && d.belowGuardCount === d.pastDateCount) {
      return `All ${d.pastDateCount} past date(s) have baseline < ${d.effectiveGuardPct}% (early-maturity guard). Max baseline: ${formatFixed(d.maxBaseline, 1)}%. SPI will appear as the project matures, or lower the threshold in KPI Configuration.`;
    }
    if (d.pastDateCount > 0) {
      return `${d.pastDateCount} past date(s) found but SPI could not be computed. Check that baseline values are populated.`;
    }
    return "";
  }, [spiHistory, series, spiDiag]);

  const currentSpi = useMemo(
    () => getCurrentSpiAsOf(spiHistory, todayISO),
    [spiHistory, todayISO],
  );
  const previousSpi = useMemo(() => {
    if (!currentSpi || spiHistory.length < 2) return null;
    const earlier = spiHistory.filter((p) => p.date < currentSpi.date);
    return earlier.length ? earlier[earlier.length - 1] : null;
  }, [spiHistory, currentSpi]);

  const band = currentSpi ? getSpiColorBand(currentSpi.spi, { warning_threshold: warnThresh, critical_threshold: critThresh }) : 0;
  const bandColorHex = indexColorHex(band);
  const bandLabel =
    band === 3 ? "On Track" : band === 2 ? "Warning" : band === 1 ? "Critical" : "—";

  // --- Normalize SPI history to generic VarianceHistoryPoint ---
  const chartData: VarianceHistoryPoint[] = useMemo(
    () => spiHistory.map((p) => ({ date: p.date, value: p.spi, belowGuard: p.belowGuard })),
    [spiHistory],
  );

  // --- Date range for x-axis alignment ---
  const fullDateRangeMin = useMemo(
    () => series.length ? series.reduce((a, b) => a.reporting_date < b.reporting_date ? a : b).reporting_date : undefined,
    [series],
  );
  const fullDateRangeMax = useMemo(
    () => series.length ? series.reduce((a, b) => a.reporting_date > b.reporting_date ? a : b).reporting_date : undefined,
    [series],
  );

  const displayTitle = title || "Schedule Variance (SPI)";

  // --- mini mode ---
  const wantsMini = displayMode === "mini";
  const showAsModal = wantsMini && fullOpen;

  if (wantsMini && !fullOpen) {
    return (
      <VarianceMiniRow
        title={displayTitle}
        currentValue={currentSpi?.spi ?? null}
        bandColorHex={bandColorHex}
        bandLabel={bandLabel}
        onExpand={() => setFullOpen(true)}
      />
    );
  }

  // --- full mode inner content (header + body, no outer wrap) ---
  const innerContent = (
    <>
      {/* Header — uses same CSS class as Progress */}
      <div className={styles.scheduleChartHeader}>
        <div className={styles.scheduleChartTitle}>
          {displayTitle}
        </div>
      </div>

      {/* Body: gauge + chart — uses same CSS class as Progress */}
      <div className={styles.scheduleChartBody}>
        {/* Left: Gauge — uses same CSS class as Progress side panel */}
        <div className={styles.scheduleSidePanel} style={{ padding: "6px 4px 4px", flex: "0 0 220px" }}>
          <PerformanceIndexGauge
            currentValue={currentSpi?.spi ?? 0}
            previousValue={previousSpi?.spi ?? null}
            warningThreshold={warnThresh}
            criticalThreshold={critThresh}
            metricLabel={undefined}
            primaryLabel="Actual Progress"
            secondaryLabel="Planned Progress"
            primaryValue={currentSpi?.actual_pct ?? null}
            secondaryValue={currentSpi?.baseline_pct ?? null}
            lastUpdatedDate={currentSpi?.date ?? null}
          />
        </div>

        {/* Right: SPI History Chart */}
        <div className={styles.scheduleChartPanel}>
          <PerformanceIndexHistoryChart
            data={chartData}
            warningThreshold={warnThresh}
            criticalThreshold={critThresh}
            asOfDate={todayISO}
            asOfColor={bandColorHex}
            fullDateRangeMin={fullDateRangeMin}
            fullDateRangeMax={fullDateRangeMax}
            emptyReason={spiEmptyReason}
            watermarkHigh="AHEAD"
            watermarkLow="BEHIND"
            tooltipLabel="SPI"
          />
        </div>
      </div>
    </>
  );

  // When embedded inside a parent container (combined Progress + SPI box),
  // skip the outer scheduleChartWrap — the parent already provides it.
  const fullContent = embedded ? innerContent : (
    <div className={styles.scheduleChartWrap}>{innerContent}</div>
  );

  // --- modal overlay (when expanded from mini) ---
  if (showAsModal) {
    return (
      <VarianceModalOverlay
        title={displayTitle}
        fullContent={fullContent}
        onCollapse={() => setFullOpen(false)}
      />
    );
  }

  return fullContent;
}
